import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import Switch from "@/components/ui/switch";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ReportSection from "./ReportSection";
import ReportChart from "./ReportChart";
import { Target } from "@/components/icons";
import { reportsApi } from "../services/reports.api";
import type {
  CategoryComparisonItem,
  CategoryComparisonCombinedItem,
  CategoryTreeNode,
  ReportFilters,
} from "../types/reports.types";
import styles from "./ReportCategoryComparison.module.scss";

interface FlatCategory {
  id: string;
  name: string;
  parentID: string | null;
  depth: number;
}

/** Ağaç → sıralı düz liste (breadth-friendly, indent için depth ile). */
function flattenTree(nodes: CategoryTreeNode[], depth = 0, parentID: string | null = null): FlatCategory[] {
  const out: FlatCategory[] = [];
  for (const n of nodes) {
    const name = n.name ?? n.label ?? "—";
    out.push({ id: n.id, name, parentID, depth });
    const children = n.subCategories ?? n.children ?? [];
    if (children.length > 0) out.push(...flattenTree(children, depth + 1, n.id));
  }
  return out;
}

type MetricKey =
  | "completionPercent"
  | "successPercent"
  | "averageScore"
  | "enrolledCount"
  | "completedCount"
  | "certificateCount";

const METRICS: { key: MetricKey; label: string; suffix?: string }[] = [
  { key: "completionPercent", label: "Tamamlanma %", suffix: "%" },
  { key: "successPercent", label: i18n.t("reportCategoryComparison.successPercent"), suffix: "%" },
  { key: "averageScore", label: "Ort. Skor" },
  { key: "enrolledCount", label: i18n.t("reportCategoryComparison.enrolled") },
  { key: "completedCount", label: "Tamamlayan" },
  { key: "certificateCount", label: "Sertifika" },
];

interface Props {
  filters: ReportFilters;
}

const ReportCategoryComparison: React.FC<Props> = ({ filters }) => {
  const { t } = useTranslation();
  const [tree, setTree] = useState<CategoryTreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [includeDescendants, setIncludeDescendants] = useState(true);
  const [metric, setMetric] = useState<MetricKey>("completionPercent");
  const [items, setItems] = useState<CategoryComparisonItem[]>([]);
  const [children, setChildren] = useState<CategoryComparisonItem[]>([]);
  const [combined, setCombined] = useState<CategoryComparisonCombinedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTreeLoading(true);
    reportsApi
      .getCategoryTree()
      .then((data) => {
        if (!cancelled) setTree(data);
      })
      .catch(() => {
        if (!cancelled) setTree([]);
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const flat = useMemo(() => flattenTree(tree), [tree]);

  useEffect(() => {
    if (selected.length === 0) {
      setItems([]);
      setChildren([]);
      setCombined([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    reportsApi
      .getCategoryComparison({ categoryIds: selected, includeDescendants, filters })
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
          setChildren(res.childrenBreakdown);
          setCombined(res.combinedByName);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setChildren([]);
          setCombined([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, includeDescendants, filters]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelected([]);

  const selectedLabels = useMemo(() => {
    const byId = new Map(flat.map((c) => [c.id, c.name]));
    return selected.map((id) => byId.get(id) ?? id);
  }, [flat, selected]);

  const chartSource = items; // Çoklu: doğrudan seçilenler. Tek: çocuklar altta ayrı gösterilir.
  const chartSeries = useMemo(() => {
    const values = chartSource.map((it) => {
      const raw = it[metric];
      return typeof raw === "number" ? raw : 0;
    });
    const meta = METRICS.find((m) => m.key === metric);
    return {
      categories: chartSource.map((it) => it.categoryName),
      series: [{ name: meta?.label ?? metric, data: values }],
    };
  }, [chartSource, metric]);

  const childrenChartSeries = useMemo(() => {
    const values = children.map((it) => {
      const raw = it[metric];
      return typeof raw === "number" ? raw : 0;
    });
    const meta = METRICS.find((m) => m.key === metric);
    return {
      categories: children.map((it) => it.categoryName),
      series: [{ name: meta?.label ?? metric, data: values }],
    };
  }, [children, metric]);

  const isPercentMetric = metric === "completionPercent" || metric === "successPercent";

  const renderMetricValue = (
    item: CategoryComparisonItem | CategoryComparisonCombinedItem,
    key: MetricKey,
  ) => {
    const v = item[key];
    if (v == null) return "—";
    if (key === "completionPercent" || key === "successPercent") return `%${v}`;
    if (key === "averageScore") return Math.round(Number(v));
    return String(v);
  };

  return (
    <ReportSection
      title={t("reportCategoryComparison.title")}
      description={t("reportCategoryComparison.description")}
      icon={<Target width={18} height={18} />}
    >
      <div className={styles.controls}>
        <div className={styles.pickerWrap}>
          <Button variant="outlined" size="small" onClick={() => setPickerOpen((v) => !v)}>
            {selected.length === 0
              ? t("reportCategoryComparison.selectCategory")
              : `${selected.length} kategori seçildi`}
          </Button>
          {selected.length > 0 && (
            <Button variant="text" size="small" onClick={clearSelection}>
              Temizle
            </Button>
          )}
          {pickerOpen && (
            <div className={styles.pickerPanel}>
              {treeLoading ? (
                <div className={styles.pickerEmpty}>{t("reportCategoryComparison.loading")}</div>
              ) : flat.length === 0 ? (
                <div className={styles.pickerEmpty}>{t("reportCategoryComparison.empty")}</div>
              ) : (
                <ul className={styles.pickerList}>
                  {flat.map((c) => (
                    <li
                      key={c.id}
                      className={styles.pickerItem}
                      style={{ paddingLeft: 8 + c.depth * 16 }}
                    >
                      <Checkbox
                        value={selected.includes(c.id)}
                        checked={selected.includes(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        label={c.name}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <label className={styles.toggleRow}>
          <Switch
            checked={includeDescendants}
            onChange={(v) => setIncludeDescendants(Boolean(v))}
          />
          <span>Alt kategorileri dahil et</span>
        </label>

        <div className={styles.metricRow}>
          <span className={styles.metricLabel}>{t("reportCategoryComparison.chartMetric")}</span>
          <div className={styles.metricChips}>
            {METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`${styles.metricChip} ${
                  metric === m.key ? styles.metricChipActive : ""
                }`}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedLabels.length > 0 && (
        <div className={styles.selectedChips}>
          {selectedLabels.map((name, idx) => (
            <span key={idx} className={styles.selectedChip}>
              {name}
            </span>
          ))}
        </div>
      )}

      {selected.length === 0 ? (
        <div className={styles.empty}>
          Karşılaştırma için en az bir kategori seçin.
        </div>
      ) : (
        <>
          <ReportChart
            title={
              selected.length === 1
                ? t("reportCategoryComparison.selectedCategorySingle")
                : t("reportCategoryComparison.selectedCategoryMulti")
            }
            type="bar"
            height={Math.max(220, chartSource.length * 48 + 80)}
            isLoading={loading}
            isEmpty={chartSource.length === 0}
            emptyMessage="Veri yok."
            series={chartSeries.series}
            options={{
              plotOptions: {
                bar: { horizontal: true, barHeight: "60%", borderRadius: 4 },
              },
              xaxis: {
                categories: chartSeries.categories,
                ...(isPercentMetric
                  ? { max: 100, labels: { formatter: (v) => `${v}%` } }
                  : {}),
              },
              dataLabels: {
                enabled: true,
                formatter: (v) => (isPercentMetric ? `${v}%` : String(v)),
                style: { fontSize: "11px", fontWeight: 600 },
              },
              colors: ["#4f46e5"],
            }}
          />

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Kategori</th>
                  <th>{t("reportCategoryComparison.educationLabel")}</th>
                  {METRICS.map((m) => (
                    <th key={m.key}>{m.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.categoryId}>
                    <td>{it.categoryName}</td>
                    <td>{it.educationCount}</td>
                    {METRICS.map((m) => (
                      <td key={m.key}>{renderMetricValue(it, m.key)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {combined.length > 0 && (
            <div className={styles.childrenBlock}>
              <h4 className={styles.childrenTitle}>
                {t("reportCategoryComparison.combinedTitle")}
              </h4>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Kategori</th>
                      <th>{t("reportCategoryComparison.educationLabel")}</th>
                      {METRICS.map((m) => (
                        <th key={m.key}>{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {combined.map((it) => (
                      <tr key={it.categoryName}>
                        <td>
                          {it.categoryName}{" "}
                          <span className={styles.childrenTitle}>
                            ({it.categoryIds.length}{" "}
                            {t("reportCategoryComparison.combinedBranchSuffix")})
                          </span>
                        </td>
                        <td>{it.educationCount}</td>
                        {METRICS.map((m) => (
                          <td key={m.key}>{renderMetricValue(it, m.key)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selected.length === 1 && children.length > 0 && (
            <div className={styles.childrenBlock}>
              <h4 className={styles.childrenTitle}>Alt Kategoriler</h4>
              <ReportChart
                type="bar"
                height={Math.max(200, children.length * 40 + 60)}
                isLoading={loading}
                isEmpty={children.length === 0}
                emptyMessage="Alt kategori verisi yok."
                series={childrenChartSeries.series}
                options={{
                  plotOptions: {
                    bar: { horizontal: true, barHeight: "60%", borderRadius: 4 },
                  },
                  xaxis: {
                    categories: childrenChartSeries.categories,
                    ...(isPercentMetric
                      ? { max: 100, labels: { formatter: (v) => `${v}%` } }
                      : {}),
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: (v) => (isPercentMetric ? `${v}%` : String(v)),
                    style: { fontSize: "11px", fontWeight: 600 },
                  },
                  colors: ["#10b981"],
                }}
              />
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Kategori</th>
                      <th>{t("reportCategoryComparison.educationLabel")}</th>
                      {METRICS.map((m) => (
                        <th key={m.key}>{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((it) => (
                      <tr key={it.categoryId}>
                        <td>{it.categoryName}</td>
                        <td>{it.educationCount}</td>
                        {METRICS.map((m) => (
                          <td key={m.key}>{renderMetricValue(it, m.key)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </ReportSection>
  );
};

export default ReportCategoryComparison;
